import React from 'react';
import { bool, func, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { get } from 'lodash';

import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import EsmsDialog from '../../components/EsmsDialog';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as DeviceActions from '../../actions/deviceActions';
import * as UiActions from '../../actions/uiActions';

import classes from './ManageBacnetDeviceModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(45)
        .required(),
    profileName: Yup.string()
        .required(),
    model: Yup.string()
        .required(),
    deviceId: Yup.string()
        .required(),
    macAddress: Yup.string()
        .required()
});

const ManageBacnetDeviceModal = ({
    close,
    open,
    title,
    isLoading,
    toggleLoading,
    errors,
    profiles,
    clearErrors,
    ...props
}) => {
    const formik = useFormik({
        initialValues: {
            name:        get(props, 'device.name', ''),
            profileName: get(props, 'device.basProfileId', ''),
            model:       get(props, 'device.model', ''),
            deviceId:    get(props, 'device.deviceId', ''),
            macAddress:  get(props, 'device.macAddress', '')
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                agent,
                companyInfo,
                manageDevice,
                device
            } = props;

            const agentId = get(agent, 'id', '');
            const companyId = get(companyInfo, 'companyId', '');
            const configurationId = get(agent, 'driverConfiguration[0].id', '');
            const basType = get(agent, 'driverConfiguration[0].basType', '');

            const deviceData = {
                basAgentId:   agentId,
                basProfileId: formik.values.profileName,
                configurationId,
                deviceId:     formik.values.deviceId,
                macAddress:   formik.values.macAddress,
                model:        formik.values.model,
                name:         formik.values.name
            };

            const connectionData = {
                agentId,
                basType,
                companyId,
                configurationId
            };

            let isUpdated;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);

                    isUpdated = await manageDevice(device.id, deviceData, connectionData);
                }

                if (isUpdated) handleClose();
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();
        close();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={close}
            disabled={formik.isSubmitting}
            onRendered={formik.handleReset}
            title={title}
            onConfirm={formik.handleSubmit}
            confirmText="Update"
            isLoading={isLoading}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelContent}>
                        <p>
                            Enter a unique Device Name.
                        </p>
                        <p>
                            Choose the Profile for the Bacnet Device. You can select a Key2Act Profile or a Custom Profile.
                        </p>
                        <p>
                            Enter the model and mac address.
                        </p>
                        <p>
                            Verify the Device Id associated with the Device.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Device Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="profileName"
                                classes={{
                                    focused: classes.cssFocused
                                }}
                                error={Boolean(formik.errors.profileName) && Boolean(formik.touched.profileName)}
                            >
                                Profile Name
                            </InputLabel>
                            <Select
                                value={formik.values.profileName}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'profileName',
                                    id:     'profileName',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.profileName) && Boolean(formik.touched.profileName)}
                            >
                                {
                                    profiles.map(profile =>
                                        (profile.basType.toLowerCase() === 'bacnetip' ||
                                            profile.basType.toLowerCase() === 'bacnetethernet') &&
                                            <MenuItem
                                                className={classes.selectMenuItem}
                                                value={profile.id}
                                                key={profile.id}
                                            >
                                                {profile.name}
                                            </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.profileName &&
                                formik.errors.profileName && <FormHelperText className={classes.error}>{formik.errors.profileName}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="model"
                                name="model"
                                type="text"
                                value={formik.values.model}
                                label="Model"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.model) && Boolean(formik.touched.model)}
                                helperText={Boolean(formik.touched.model) && formik.errors.model}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="deviceId"
                                name="deviceId"
                                type="text"
                                value={formik.values.deviceId}
                                label="Device Id"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.deviceId) && Boolean(formik.touched.deviceId)}
                                helperText={Boolean(formik.touched.deviceId) && formik.errors.deviceId}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="macAddress"
                                name="macAddress"
                                type="text"
                                value={formik.values.macAddress}
                                label="MAC"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.macAddress) && Boolean(formik.touched.macAddress)}
                                helperText={Boolean(formik.touched.macAddress) && formik.errors.macad}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

ManageBacnetDeviceModal.defaultTypes = {
    close: () => { },
    open:  false,
    title: ''
};

ManageBacnetDeviceModal.propTypes = {
    close: func.isRequired,
    open:  bool.isRequired,
    title: string.isRequired
};

const mapStateToProps = state => {
    return {
        companyInfo: state.companyInfo,
        errors:      state.messages.errors,
        isLoading:   state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...DeviceActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageBacnetDeviceModal);
